import { ColDef } from 'ag-grid-community';
import { BudgetBys, BudgetExpenseOrIncome } from 'business/constants';
import {
    GESTIONNAIRE_CODE_FIELD,
    DEPASSEMENT_METRIC,
    CADRAGE,
    SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
    SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
    SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';
import { BudgetProposalVentilationBy } from 'routes/BudgetModule/config/queries';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    getResourceNameField,
    getResourceSoftwareIdField,
} from 'routes/BudgetModule/helpers/budgetProposalHelpers';

import { percentageGetter, valueGetter } from 'ui/AgGridReact/getters';
import {
    budgetCurrencyFormatter,
    budgetPercentageFormatter,
} from 'ui/AgGridReact/formatters';
import {
    getDepassementColor,
    getDepassementTooltip,
} from 'routes/BudgetModule/helpers/format';
import { getNoResourceText } from 'routes/BudgetModule/components/BudgetProposal/helpers/dataHelpers';
import { getUrl as getOperationUrl } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/Operations/helpers/gridHelpers';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';

const getBudgetVentilatiedByResourceColDefs = (
    budgetProposal: BudgetProposalT,
) => {
    // encodeURIComponent is necessary for names like "URBANISME/FONCIER" that break the URL
    const getUrl = (rowData: ResponseDataT) => {
        const resourceSoftwareIdField = getResourceSoftwareIdField(
            budgetProposal.by,
        );
        let selectResourceSoftwareId = rowData[resourceSoftwareIdField];
        if (!selectResourceSoftwareId) {
            // in case we have a null value, we replace by NO SERVICE/GESTIONNAIRE
            selectResourceSoftwareId = getNoResourceText(budgetProposal.by);
        }
        return `/budget_proposal/${
            budgetProposal.id
        }/resource/${encodeURIComponent(
            // @ts-expect-error selectResourceSoftwareId might be a Date, which is not assignable to a string
            // To fix it, we need to avoid using ResponseDataT everywhere
            selectResourceSoftwareId,
        )}/expense/imputations`;
    };

    // Disable lines & button with no gestionnaire/org_chart
    const getDisabled = (rowData: ResponseDataT) => {
        const resourceSoftwareIdField = getResourceSoftwareIdField(
            budgetProposal.by,
        );
        return !rowData[resourceSoftwareIdField];
    };

    const columns: ColDef[] = [];

    if (budgetProposal.by === BudgetBys.GESTIONNAIRE) {
        columns.push({
            field: GESTIONNAIRE_CODE_FIELD,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            cellRenderer: 'linkCellRenderer',
            cellRendererParams: { getUrl },
        });
    }

    columns.push(
        {
            field: budgetProposal.by,
            valueGetter: (params) => {
                const resourceNameField = getResourceNameField(
                    budgetProposal.by,
                    false,
                );
                if (params.data && params.data[resourceNameField])
                    return params.data[resourceNameField];
                // in case we have a null value, we replace by NO SERVICE/GESTIONNAIRE
                // to avoid displaying a blank cell
                return getNoResourceText(budgetProposal.by);
            },
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            sort: 'asc',
            cellRenderer: 'linkCellRenderer',
            cellRendererParams: {
                getUrl,
                getDisabled,
            },
        },
        {
            field: BudgetExpenseOrIncome.EXPENSE,
            sortable: true,
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetCurrencyFormatter,
            valueGetter: (params) => params.data.expense,
            type: 'numericColumn',
        },
        {
            field: BudgetExpenseOrIncome.INCOME,
            sortable: true,
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetCurrencyFormatter,
            valueGetter: (params) => params.data.income,
            type: 'numericColumn',
        },
    );
    columns.push(
        {
            field: CADRAGE,
            sortable: true,
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetCurrencyFormatter,
            filterValueGetter: valueGetter,
            type: 'numericColumn',
        },
        {
            field: DEPASSEMENT_METRIC,
            sortable: true,
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetPercentageFormatter,
            filterValueGetter: percentageGetter,
            cellRenderer: 'chipCellRenderer',
            cellRendererParams: {
                getColor: getDepassementColor,
                getTooltip: getDepassementTooltip,
            },
            type: 'numericColumn',
        },
    );
    columns.push({
        field: '',
        cellRenderer: 'buttonLinkCellRenderer',
        cellRendererParams: {
            getUrl,
            getDisabled,
        },
        suppressColumnsToolPanel: true,
    });
    return columns;
};

const getBudgetVentilatiedByOperationColDefs = (
    budgetProposal: BudgetProposalT,
): ColDef[] => {
    // Disable lines & button with no operation
    const getDisabled = (rowData: ResponseDataT) =>
        !rowData[SECONDARY_BUDGET_AXIS_ELEMENT_CODE] ||
        !rowData[SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID];

    const onOperationClicked = (row: ResponseDataT) => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.operationClicked,
            {
                exercice: budgetProposal.exercice,
                budgetProposal: budgetProposal.name,
                section: budgetProposal.section,
                operation: row[SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD],
            },
        );
    };
    const NO_OPERATION = '- PAS DE VALEUR';
    return [
        {
            field: SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            valueFormatter: budgetCurrencyFormatter,
            filterValueGetter: valueGetter,
            cellRenderer: 'linkCellRenderer',
            cellRendererParams: {
                getUrl: getOperationUrl,
                getDisabled,
                onClick: onOperationClicked,
            },
        },
        {
            field: SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            sort: 'asc',
            valueFormatter: budgetCurrencyFormatter,
            valueGetter: (params) =>
                params.data[SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD] !== ''
                    ? valueGetter(params)
                    : NO_OPERATION,
            filterValueGetter: (params) =>
                params.data[SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD] !== ''
                    ? valueGetter(params)
                    : NO_OPERATION,
            cellRenderer: 'linkCellRenderer',
            cellRendererParams: {
                getUrl: getOperationUrl,
                getDisabled,
                onClick: onOperationClicked,
            },
        },
        {
            field: BudgetExpenseOrIncome.EXPENSE,
            sortable: true,
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetCurrencyFormatter,
            valueGetter: (params) => params.data.expense,
        },
        {
            field: BudgetExpenseOrIncome.INCOME,
            sortable: true,
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetCurrencyFormatter,
            valueGetter: (params) => params.data.income,
        },
        {
            field: '',
            cellRenderer: 'buttonLinkCellRenderer',
            cellRendererParams: {
                getUrl: getOperationUrl,
                getDisabled,
                onClick: onOperationClicked,
            },
            suppressColumnsToolPanel: true,
        },
    ];
};

export const getColDefs = (
    budgetProposal: BudgetProposalT,
    ventilationBy: BudgetProposalVentilationBy,
) => {
    if (ventilationBy === 'resource') {
        return getBudgetVentilatiedByResourceColDefs(budgetProposal);
    }
    if (ventilationBy === 'operation') {
        return getBudgetVentilatiedByOperationColDefs(budgetProposal);
    }
    return [];
};
